import { ChangeEvent } from 'react';

import { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';

import InputMask from '../../ReactInputMask';
import { Preloader } from '../common';

/**
 * Свойства компонента {@link TextFieldMasked}
 */
interface TextFieldMaskedOwnProps {
  /**
   * Маска, которая будет применена к полю ввода.
   */
  mask: string;

  /**
   * Если true, поле ввода будет показывать индикатор загрузки.
   */
  loading?: boolean;

  /**
   * Классы компонента {@link TextField}.
   */
  classes: TextFieldProps['classes'];

  /**
   * Функция обратного вызова для обработки изменений значения ввода.
   */
  onChange: (event: ChangeEvent<HTMLInputElement>, nextValue: string) => void;
}

type TextFieldMaskedProps = TextFieldMaskedOwnProps &
  Omit<AutocompleteRenderInputParams, 'id'> &
  Pick<TextFieldProps, 'name' | 'error' | 'helperText' | 'placeholder'> & {
    /**
     * Идентификатор поля ввода.
     */
    id?: string;
  };

/**
 * TextFieldMasked — компонент, который предоставляет поле ввода с маской, используя Material-UI's TextField.
 * Он поддерживает различные форматы масок и может отображать индикатор загрузки.
 *
 * @internal
 * @param mask - Маска, которая будет применена к полю ввода.
 * @param loading - Если true, поле ввода будет показывать индикатор загрузки.
 * @param classes - Классы компонента {@link TextField}.
 * @param onChange - Функция обратного вызова для обработки изменений значения ввода.
 * @param name - Атрибут name для поля ввода.
 * @param error - Если true, поле ввода будет отмечено как с ошибкой.
 * @param helperText - Вспомогательный текст, который будет отображаться с полем ввода.
 * @param placeholder - Текст плейсхолдера.
 * @param rest - Остальные свойства, передаваемые в {@link TextField}.
 * @param id - Атрибут ID для поля ввода.
 * @param inputProps - Свойства элемента input, переданные Autocomplete.
 * @param InputProps - Свойства обёртки Input, переданные Autocomplete.
 * @param InputLabelProps - Свойства метки поля, переданные Autocomplete.
 * @returns JSX компонента.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link TextFieldMaskedProps}
 */
export const TextFieldMasked = ({
  id,
  mask,
  name,
  error,
  helperText,
  placeholder,
  loading,
  onChange,
  inputProps,
  InputProps,
  InputLabelProps,
  classes,
  ...rest
}: TextFieldMaskedProps) => {
  const {
    value,
    onFocus,
    onBlur,
    onMouseDown,
    onKeyDown,
    ref: inputRef,
    ...inputPropsRest
  } = inputProps;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event, event.target.value);
    inputProps.onChange?.(event);
  };

  return (
    <InputMask
      value={value}
      onFocus={onFocus}
      onBlur={onBlur}
      onMouseDown={onMouseDown}
      onKeyDown={onKeyDown}
      mask={mask}
      name={name}
      onChange={handleChange}
      disabled={rest.disabled}
    >
      <TextField
        {...rest}
        id={id}
        inputRef={inputRef}
        name={name}
        classes={classes}
        autoComplete="off"
        variant="outlined"
        fullWidth
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        slotProps={{
          htmlInput: {
            ...inputPropsRest,
          },
          input: {
            ...InputProps,
            ...(loading ? { endAdornment: <Preloader /> } : {}),
          },
          inputLabel: {
            ...InputLabelProps,
          },
        }}
      />
    </InputMask>
  );
};
